import type { DestinationId } from "./destinations";
import type { ShortcutIcon } from "./shortcuts";

/** Rubriques de l'écran « Ajouter un raccourci », dans l'ordre d'affichage. */
export const SHORTCUT_GROUPS = [
  { id: "create", labelKey: "bo.home.shortcutGroup.create", label: "Créer" },
  {
    id: "search",
    labelKey: "bo.home.shortcutGroup.search",
    label: "Rechercher",
  },
  {
    id: "website",
    labelKey: "bo.home.shortcutGroup.website",
    label: "Site internet",
  },
  {
    id: "data",
    labelKey: "bo.home.shortcutGroup.data",
    label: "Import et export",
  },
  {
    id: "access",
    labelKey: "bo.home.shortcutGroup.access",
    label: "Accès rapide",
  },
] as const;

export type ShortcutGroup = (typeof SHORTCUT_GROUPS)[number]["id"];

/** Actions proposées dans « Ajouter un raccourci » (ajout, recherche, navigation). */
export type CatalogShortcut = {
  id: string;
  labelKey: string;
  label: string;
  icon: ShortcutIcon;
  group: ShortcutGroup;
  destination: DestinationId;
};

export const SHORTCUT_CATALOG: CatalogShortcut[] = [
  {
    id: "artwork.new",
    labelKey: "bo.home.shortcut.artworkNew",
    label: "Enregistrer une oeuvre",
    icon: "bookmark",
    group: "create",
    destination: "artworkNew",
  },
  {
    id: "artwork.search",
    labelKey: "bo.home.shortcut.artworkSearch",
    label: "Rechercher une oeuvre",
    icon: "search",
    group: "search",
    destination: "artworkSearch",
  },
  {
    id: "news.new",
    labelKey: "bo.home.shortcut.newsNew",
    label: "Ajouter une news - site internet",
    icon: "plus",
    group: "website",
    destination: "news",
  },
  {
    id: "selection.edit",
    labelKey: "bo.home.shortcut.selectionEdit",
    label: "Modifier la sélection d’oeuvres",
    icon: "pencil",
    group: "website",
    destination: "selections",
  },
  {
    id: "collector.search",
    labelKey: "bo.home.shortcut.collectorSearch",
    label: "Rechercher un contact",
    icon: "search",
    group: "search",
    destination: "collectorSearch",
  },
  {
    id: "collector.new",
    labelKey: "bo.home.shortcut.collectorNew",
    label: "Ajouter un contact",
    icon: "plus",
    group: "create",
    destination: "collectorNew",
  },
  {
    id: "exhibition.new",
    labelKey: "bo.home.shortcut.exhibitionNew",
    label: "Enregistrer exposition",
    icon: "bookmark",
    group: "create",
    destination: "exhibitionNew",
  },
  {
    id: "import.artwork",
    labelKey: "bo.home.shortcut.import",
    label: "Importer des données",
    icon: "upload",
    group: "data",
    destination: "importArtworks",
  },
  {
    id: "author.new",
    labelKey: "bo.home.shortcut.authorNew",
    label: "Ajouter un auteur",
    icon: "user-plus",
    group: "create",
    destination: "authorNew",
  },
  {
    id: "bibliography.new",
    labelKey: "bo.home.shortcut.bibliographyNew",
    label: "Ajouter une bibliographie",
    icon: "book",
    group: "create",
    destination: "bibliographyNew",
  },
  {
    id: "auction.new",
    labelKey: "bo.home.shortcut.auctionNew",
    label: "Ajouter une vente publique",
    icon: "gavel",
    group: "create",
    destination: "auctionNew",
  },
  {
    id: "multipleprint.new",
    labelKey: "bo.home.shortcut.multipleprintNew",
    label: "Ajouter un multiple",
    icon: "layers",
    group: "create",
    destination: "multipleprintNew",
  },
  {
    id: "photography.new",
    labelKey: "bo.home.shortcut.photographyNew",
    label: "Ajouter une photographie",
    icon: "camera",
    group: "create",
    destination: "photographyNew",
  },
  {
    id: "audiovisual.new",
    labelKey: "bo.home.shortcut.audiovisualNew",
    label: "Ajouter un audiovisuel",
    icon: "film",
    group: "create",
    destination: "audiovisualNew",
  },
  {
    id: "writing.new",
    labelKey: "bo.home.shortcut.writingNew",
    label: "Ajouter un écrit",
    icon: "pen-line",
    group: "create",
    destination: "writingNew",
  },
  {
    id: "document.new",
    labelKey: "bo.home.shortcut.documentNew",
    label: "Ajouter un document",
    icon: "file-plus",
    group: "create",
    destination: "documentNew",
  },
  {
    id: "export",
    labelKey: "bo.home.shortcut.export",
    label: "Exporter des données",
    icon: "download",
    group: "data",
    destination: "exports",
  },
  {
    id: "notes",
    labelKey: "bo.home.shortcut.notes",
    label: "Mes notes",
    icon: "file-text",
    group: "access",
    destination: "notes",
  },
  {
    id: "website",
    labelKey: "bo.home.shortcut.website",
    label: "Mon site internet",
    icon: "globe",
    group: "website",
    destination: "website",
  },
  {
    id: "catalogue",
    labelKey: "bo.home.shortcut.catalogue",
    label: "Site catalogue raisonné",
    icon: "book",
    group: "website",
    destination: "catalogueRaisonne",
  },
  {
    id: "authentications",
    labelKey: "bo.home.shortcut.authentications",
    label: "Authentifications",
    icon: "shield-check",
    group: "website",
    destination: "authentications",
  },
  {
    id: "eshop",
    labelKey: "bo.home.shortcut.eshop",
    label: "E-commerce",
    icon: "shopping-cart",
    group: "website",
    destination: "eshop",
  },
  {
    id: "trash",
    labelKey: "bo.home.shortcut.trash",
    label: "Corbeille",
    icon: "trash",
    group: "access",
    destination: "trash",
  },
  {
    id: "settings",
    labelKey: "bo.home.shortcut.settings",
    label: "Mes réglages",
    icon: "settings",
    group: "access",
    destination: "settings",
  },
];

export function findCatalogShortcut(id: string): CatalogShortcut | undefined {
  return SHORTCUT_CATALOG.find((entry) => entry.id === id);
}
